import { Box, FormControl, FormErrorMessage, FormLabel, Input } from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import allDrugData from '../../data/drugPricing.json'; // Import the local JSON
import { debounce } from '../../utils/debounce';
import DosageSelect from './DosageSelect';

type Dosage = {
  id: string;
  label: string;
  defaultQuantity: number;
  priceMultiplier: number;
};

type Drug = {
  id: string;
  name: string;
  dosages: Dosage[];
  selectedDosage?: Dosage;
  basePricing?: any;
};

type DrugSearchProps = {
  onDrugSelect: (drug: Drug | null) => void;
  error?: string;
  externallySelectedDrug?: Drug | null;
};

export default function DrugSearch({ onDrugSelect, error, externallySelectedDrug }: DrugSearchProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<Drug[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedDrug, setSelectedDrug] = useState<Drug | null>(null);
  const [selectedDosageId, setSelectedDosageId] = useState('');
  const [isInputFocused, setIsInputFocused] = useState(false);

  const drugError = error === 'Please select a drug' ? error : undefined;
  const dosageError = error === 'Please select a dosage' ? error : undefined;

  // Create a debounced search function
  const debouncedSearch = debounce((searchValue: string) => {
    if (searchValue) {
      setIsLoading(true);
      const lowercasedFilter = searchValue.toLowerCase();
      const filteredDrugs = allDrugData.filter((drug: Drug) => drug.name.toLowerCase().includes(lowercasedFilter));
      setSearchResults(filteredDrugs as Drug[]);
      setIsLoading(false);
    } else if (isInputFocused) {
      setSearchResults(allDrugData as Drug[]);
    } else {
      setSearchResults([]);
    }
  }, 300);

  useEffect(() => {
    debouncedSearch(searchTerm);
  }, [searchTerm, debouncedSearch]);

  // Effect to handle externally selected drug
  useEffect(() => {
    if (externallySelectedDrug) {
      setSelectedDrug(externallySelectedDrug);
      setSearchTerm(externallySelectedDrug.name);
      setSelectedDosageId(externallySelectedDrug.selectedDosage?.id || '');
      setSearchResults([]); // Clear search results as a drug is now "selected"
      setIsInputFocused(false);
    } else if (!searchTerm && selectedDrug) {
      setSelectedDrug(null);
      setSelectedDosageId('');
    }
  }, [externallySelectedDrug, searchTerm, selectedDrug]);

  const handleDrugSelect = (drug: Drug) => {
    let drugWithFirstDosageSelected = drug;
    if (drug.dosages && drug.dosages.length > 0) {
      drugWithFirstDosageSelected = { ...drug, selectedDosage: drug.dosages[0] };
      setSelectedDosageId(drug.dosages[0].id);
    } else {
      setSelectedDosageId(''); // No dosages, so clear any selected dosage ID
    }

    setSelectedDrug(drugWithFirstDosageSelected);
    setSearchTerm(drugWithFirstDosageSelected.name);
    setSearchResults([]);
    setIsInputFocused(false);

    onDrugSelect(drugWithFirstDosageSelected); // Pass drug with dosage selected
  };

  const handleDosageSelect = (dosageId: string) => {
    setSelectedDosageId(dosageId);
    if (selectedDrug) {
      const currentSelectedDosage = selectedDrug.dosages.find((d) => d.id === dosageId);
      if (currentSelectedDosage) {
        const drugWithSelectedDosage = { ...selectedDrug, selectedDosage: currentSelectedDosage };
        onDrugSelect(drugWithSelectedDosage);
      }
    }
  };

  const handleInputFocus = () => {
    setIsInputFocused(true);
    // If search term is already empty when focusing, load all drugs
    if (!searchTerm) {
      setSearchResults(allDrugData as Drug[]);
    }
  };

  const handleInputBlur = () => {
    // Delay hiding results to allow click on them
    setTimeout(() => {
      setIsInputFocused(false);
      // If the input is blurred and the searchTerm does not match the selectedDrug's name,
      // and a drug was previously selected, it implies the user might have manually cleared
      // or changed the text without picking from the list. In this case, we should
      // treat it as if no drug is selected or clear the selection.
      // However, this might conflict with the user just clicking away.
      // For now, we won't automatically clear selection on blur if text doesn't match.
      // The clearing logic is primarily in the input's onChange.
    }, 150);
  };

  return (
    <Box width="100%">
      <FormControl isInvalid={!!drugError}>
        <FormLabel>Drug Name</FormLabel>
        <Input
          type="text"
          value={searchTerm}
          onChange={(e) => {
            const newSearchTerm = e.target.value;
            setSearchTerm(newSearchTerm);
            if (selectedDrug && newSearchTerm !== selectedDrug.name) {
              setSelectedDrug(null);
              setSelectedDosageId('');
              onDrugSelect(null); // Notify parent that selection is cleared
              // If clearing selection by typing, ensure the dropdown can appear if input becomes empty
              if (newSearchTerm === '') {
                setIsInputFocused(true); // Keep/set focus to allow all drugs list to show
                setSearchResults(allDrugData as Drug[]);
              }
            } else if (newSearchTerm === '') {
              // If search term is made empty, show all drugs
              setIsInputFocused(true);
              setSearchResults(allDrugData as Drug[]);
              // If it was cleared and there was a selected drug, notify parent
              if (selectedDrug) {
                setSelectedDrug(null);
                setSelectedDosageId('');
                onDrugSelect(null);
              }
            }
          }}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          placeholder="Type or click to see all drugs..."
          width="100%"
        />
        {drugError && <FormErrorMessage>{drugError}</FormErrorMessage>}
      </FormControl>

      {isLoading && <Box mt={2}>Loading...</Box>}

      {isInputFocused && searchResults.length > 0 && !selectedDrug && (
        <Box
          maxHeight="200px"
          overflowY="auto"
          marginTop="10px"
          border="1px solid"
          borderColor="gray.200"
          borderRadius="md"
          zIndex="dropdown"
        >
          {searchResults.map((drug) => (
            <Box
              key={drug.id}
              onClick={() => handleDrugSelect(drug)}
              padding="8px"
              cursor="pointer"
              _hover={{ backgroundColor: 'gray.50' }}
            >
              {drug.name}
            </Box>
          ))}
        </Box>
      )}

      {selectedDrug && (
        <Box marginTop="20px">
          <DosageSelect
            value={selectedDosageId}
            onChange={handleDosageSelect}
            dosages={selectedDrug.dosages}
            error={dosageError}
          />
        </Box>
      )}
    </Box>
  );
}
